import { LoadingButton } from "@mui/lab";
import { Box, InputAdornment, Link, Stack } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import Iconify from "src/components/Iconify";
import Password from "src/components/Password";
import ChoosePlan from "src/components/choose-plan";
import {
  FormProvider,
  RHFCheckbox,
  RHFTextField,
} from "src/components/hook-form";
import Translate from "src/components/translate";
import GoogleOAuthButton from "src/pages/auth/components/google-login";
import { PATH_AUTH } from "src/routes/paths";
import AuthHelper from "./components/authHelper";
import EnterOtp from "./components/enter-otp";
import ErrorBanner from "./components/error-banner";
import useLogin from "./hooks/use-login";
import { useTranslation } from "react-i18next";

const inputStyle = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "14px",
    transition: "all 0.2s ease-in-out",
    bgcolor: (theme) =>
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.03)"
        : alpha(theme.palette.grey[500], 0.04),
    "&:hover": {
      bgcolor: (theme) =>
        theme.palette.mode === "dark"
          ? "rgba(255, 255, 255, 0.06)"
          : alpha(theme.palette.grey[500], 0.06),
    },
    "&.Mui-focused": {
      bgcolor: (theme) =>
        theme.palette.mode === "dark" ? "#161c24" : "#ffffff",
      boxShadow: (theme) =>
        `0 0 0 3.5px ${alpha(theme.palette.primary.main, 0.12)}`,
    },
    "& input:-webkit-autofill": {
      WebkitBoxShadow: (theme) =>
        theme.palette.mode === "dark"
          ? "0 0 0 100px #18222a inset !important"
          : "0 0 0 100px #ffffff inset !important",
      WebkitTextFillColor: "inherit !important",
      borderRadius: "inherit",
    },
  },
};

const LoginForm = () => {
  const { t } = useTranslation();

  const { onSubmit, methods } = useLogin();

  const {
    watch,
    formState: { isSubmitting },
  } = methods;

  const data = watch();

  return (
    <>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <Stack spacing={2} mb={2}>
          <AuthHelper />
          <ErrorBanner />
        </Stack>

        <Box sx={{ display: "flex", flexDirection: "column", textAlign: "left" }}>
          <Stack spacing={2.25}>
            <RHFTextField
              name="email"
              label={`${t("register.email")} ${t("global.or")} ${t(
                "global.username"
              )}`}
              placeholder="name@example.com / username"
              InputLabelProps={{ shrink: true }}
              sx={inputStyle}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify
                      icon="eva:person-outline"
                      sx={{ color: "text.disabled", width: 20, height: 20 }}
                    />
                  </InputAdornment>
                ),
              }}
            />
            <Password
              name="password"
              label="register.password"
              placeholder="••••••••"
              InputLabelProps={{ shrink: true }}
              sx={inputStyle}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify
                      icon="eva:lock-outline"
                      sx={{ color: "text.disabled", width: 20, height: 20 }}
                    />
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={inputStyle}>
              <ChoosePlan />
            </Box>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ my: 1.5 }}
          >
            <RHFCheckbox
              name="remember"
              label="register.remember"
              sx={{
                "& .MuiCheckbox-root": {
                  p: 0.75,
                  "&.Mui-checked": {
                    color: "primary.main",
                  },
                },
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.85rem",
                  color: "text.secondary",
                  fontWeight: 500,
                },
              }}
            />
            <Link
              component={RouterLink}
              variant="subtitle2"
              sx={{
                fontSize: "0.85rem",
                fontWeight: 600,
                color: "primary.main",
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
              to={PATH_AUTH.resetPassword}
            >
              <Translate>register.forgot</Translate>
            </Link>
          </Stack>

          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
            name="login"
            startIcon={<Iconify icon="eva:log-in-fill" width={22} height={22} />}
            sx={{
              mt: 1,
              py: 1.4,
              fontSize: "1rem",
              fontWeight: 800,
              borderRadius: "16px",
              textTransform: "none",
              letterSpacing: "0.2px",
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #1e3a6d 100%)`,
              color: "#ffffff",
              boxShadow: (theme) =>
                `0 8px 24px -4px ${alpha(theme.palette.primary.main, 0.4)}`,
              transition: "all 0.25s ease-in-out",
              "&:hover": {
                background: (theme) =>
                  `linear-gradient(135deg, ${
                    theme.palette.primary.dark || "#0a1d3b"
                  } 0%, #162f59 100%)`,
                boxShadow: (theme) =>
                  `0 12px 28px -4px ${alpha(
                    theme.palette.primary.main,
                    0.55
                  )}`,
                transform: "translateY(-1.5px)",
              },
              "&:active": {
                transform: "translateY(0)",
              },
            }}
          >
            <Translate>register.login</Translate>
          </LoadingButton>

          <GoogleOAuthButton
            plan={methods.getValues("plan")}
            buttonLabel="register.google_login"
          />
        </Box>
      </FormProvider>

      <EnterOtp data={data} />
    </>
  );
};

export default LoginForm;
